import { useEffect, useState } from 'react'
import { adminApi } from '@/services/adminApi'
import type {
    CommExperiment,
    CommExperimentStatus,
    ExperimentVariant,
    VariantStats,
} from '@/types/communications'
import {
    Badge,
    Card,
    CardContent,
    CardHeader,
    CardTitle,
    ConfirmDialog,
    EmptyState,
    Field,
    Input,
    LoadingPanel,
    PillButton,
    SectionTitle,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from './_shared'
import { RichTextEditor } from '../../components/editor/RichTextEditor'
import {
    FlaskConical,
    Plus,
    Play,
    Pause,
    Square,
    Trash2,
    ChevronLeft,
    Activity,
    X,
} from 'lucide-react'

const STATUS_VARIANT: Record<
    CommExperimentStatus,
    'default' | 'success' | 'warning' | 'error' | 'info'
> = {
    DRAFT: 'default',
    RUNNING: 'success',
    PAUSED: 'warning',
    ENDED: 'default',
}

// Use user.*-prefixed keys so the "Insert variable" menu produces markers
// the renderer auto-binds per-recipient at send time.
const VARIANT_VARIABLES = [
    { key: 'user.firstName', label: 'First name' },
    { key: 'user.name', label: 'Full name' },
    { key: 'user.email', label: 'Email' },
    { key: 'user.orgName', label: 'Organization name' },
]

export function ExperimentsTab() {
    const [items, setItems] = useState<CommExperiment[]>([])
    const [loading, setLoading] = useState(true)
    const [creating, setCreating] = useState(false)
    const [active, setActive] = useState<CommExperiment | null>(null)

    const load = async () => {
        setLoading(true)
        try {
            const r = await adminApi.getExperiments()
            setItems(r.items)
        } finally {
            setLoading(false)
        }
    }

    useEffect(() => {
        load()
    }, [])

    if (creating) {
        return (
            <ExperimentEditor
                onCancel={() => setCreating(false)}
                onSaved={async created => {
                    await load()
                    setCreating(false)
                    setActive(created)
                }}
            />
        )
    }

    if (active) {
        return (
            <ExperimentDetail
                experiment={active}
                onBack={() => setActive(null)}
                onUpdated={async () => {
                    await load()
                    const fresh = (await adminApi.getExperiments()).items.find(
                        e => e.id === active.id
                    )
                    if (fresh) setActive(fresh)
                    else setActive(null)
                }}
            />
        )
    }

    return (
        <div className="space-y-6">
            <SectionTitle
                title="Experiments"
                description="A/B test subject lines and bodies. Variants are deterministically assigned per user; results aggregate from delivery rows."
                actions={
                    <PillButton variant="primary" onClick={() => setCreating(true)}>
                        <Plus className="h-3.5 w-3.5" /> New experiment
                    </PillButton>
                }
            />

            {loading ? (
                <LoadingPanel label="Loading experiments…" />
            ) : items.length === 0 ? (
                <EmptyState
                    icon={FlaskConical}
                    title="No experiments yet"
                    description="Create one and reference its key from a broadcast or campaign to start collecting variants."
                    action={
                        <PillButton variant="primary" onClick={() => setCreating(true)}>
                            <Plus className="h-3.5 w-3.5" /> Create your first experiment
                        </PillButton>
                    }
                />
            ) : (
                <div className="space-y-3">
                    {items.map(e => (
                        <button
                            key={e.id}
                            onClick={() => setActive(e)}
                            className="w-full rounded-lg border border-gray-200 bg-white p-4 text-left transition-all hover:-translate-y-0.5 hover:border-blue-400 hover:shadow-md"
                        >
                            <div className="flex items-start justify-between">
                                <div>
                                    <div className="mb-1 flex items-center gap-2">
                                        <code className="font-mono text-xs text-blue-600">
                                            {e.key}
                                        </code>
                                        <Badge variant={STATUS_VARIANT[e.status]}>
                                            {e.status.toLowerCase()}
                                        </Badge>
                                    </div>
                                    <div className="text-xs text-gray-600">
                                        {e.variants.length} variants · created{' '}
                                        {new Date(e.createdAt).toLocaleDateString()}
                                    </div>
                                </div>
                                <div className="text-right text-xs text-gray-500">
                                    {e.startedAt && (
                                        <div>
                                            Started{' '}
                                            {new Date(e.startedAt).toLocaleDateString()}
                                        </div>
                                    )}
                                    {e.endedAt && (
                                        <div>
                                            Ended{' '}
                                            {new Date(e.endedAt).toLocaleDateString()}
                                        </div>
                                    )}
                                </div>
                            </div>
                        </button>
                    ))}
                </div>
            )}
        </div>
    )
}

function ExperimentDetail({
    experiment,
    onBack,
    onUpdated,
}: {
    experiment: CommExperiment
    onBack: () => void
    onUpdated: () => void | Promise<void>
}) {
    const [stats, setStats] = useState<VariantStats[] | null>(null)
    const [refreshing, setRefreshing] = useState(false)
    const [confirmDelete, setConfirmDelete] = useState(false)
    const [deleting, setDeleting] = useState(false)

    const loadStats = async () => {
        setRefreshing(true)
        try {
            const r = await adminApi.getExperimentResults(experiment.key)
            setStats(r.stats)
        } finally {
            setRefreshing(false)
        }
    }

    useEffect(() => {
        loadStats()
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [experiment.id])

    const setStatus = async (status: CommExperimentStatus) => {
        await adminApi.updateExperiment(experiment.id, { status })
        await onUpdated()
    }

    const remove = async () => {
        setDeleting(true)
        try {
            await adminApi.deleteExperiment(experiment.id)
            onBack()
        } finally {
            setDeleting(false)
            setConfirmDelete(false)
        }
    }

    return (
        <div className="space-y-6">
            <button
                onClick={onBack}
                className="flex items-center gap-1 text-sm text-gray-600 hover:text-gray-900"
            >
                <ChevronLeft className="h-4 w-4" /> Back to experiments
            </button>

            <SectionTitle
                title={experiment.key}
                description={`Status: ${experiment.status.toLowerCase()} · ${experiment.variants.length} variants`}
                actions={
                    <>
                        {experiment.status === 'DRAFT' && (
                            <PillButton
                                variant="primary"
                                onClick={() => setStatus('RUNNING')}
                            >
                                <Play className="h-3.5 w-3.5" /> Start
                            </PillButton>
                        )}
                        {experiment.status === 'RUNNING' && (
                            <PillButton onClick={() => setStatus('PAUSED')}>
                                <Pause className="h-3.5 w-3.5" /> Pause
                            </PillButton>
                        )}
                        {experiment.status === 'PAUSED' && (
                            <PillButton
                                variant="primary"
                                onClick={() => setStatus('RUNNING')}
                            >
                                <Play className="h-3.5 w-3.5" /> Resume
                            </PillButton>
                        )}
                        {(experiment.status === 'RUNNING' ||
                            experiment.status === 'PAUSED') && (
                            <PillButton onClick={() => setStatus('ENDED')}>
                                <Square className="h-3.5 w-3.5" /> End
                            </PillButton>
                        )}
                        <PillButton variant="danger" onClick={() => setConfirmDelete(true)}>
                            <Trash2 className="h-3.5 w-3.5" /> Delete
                        </PillButton>
                    </>
                }
            />

            <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
                {experiment.variants.map(v => (
                    <Card key={v.id}>
                        <CardContent>
                            <div className="mb-3 flex items-center justify-between">
                                <div className="text-lg font-semibold text-gray-900">
                                    Variant {v.id}
                                </div>
                                <Badge variant="info">{v.weight}%</Badge>
                            </div>
                            {v.subject && (
                                <div className="mb-3">
                                    <p className="text-[10px] font-medium uppercase tracking-wider text-gray-500">
                                        Subject
                                    </p>
                                    <p className="mt-0.5 text-sm text-gray-900">
                                        {v.subject}
                                    </p>
                                </div>
                            )}
                            {v.bodyHtml && (
                                <div>
                                    <p className="text-[10px] font-medium uppercase tracking-wider text-gray-500">
                                        Body preview
                                    </p>
                                    <div
                                        className="prose prose-sm mt-1 max-h-32 overflow-y-auto rounded border border-gray-200 bg-gray-50 p-2 text-xs"
                                        dangerouslySetInnerHTML={{ __html: v.bodyHtml }}
                                    />
                                </div>
                            )}
                        </CardContent>
                    </Card>
                ))}
            </div>

            <SectionTitle
                title="Results"
                description="Open + click rates aggregated from delivery rows tagged with this experiment."
                actions={
                    <PillButton onClick={loadStats} disabled={refreshing}>
                        <Activity className="h-3.5 w-3.5" />
                        {refreshing ? 'Refreshing…' : 'Refresh'}
                    </PillButton>
                }
            />

            {!stats ? (
                <LoadingPanel label="Loading results…" />
            ) : stats.length === 0 ? (
                <EmptyState
                    icon={Activity}
                    title="No data yet"
                    description="Once a broadcast or campaign sends with this experiment key, results will appear here."
                />
            ) : (
                <Card>
                    <CardContent className="!p-0">
                        <Table>
                            <TableHeader>
                                <TableRow>
                                    <TableHead>Variant</TableHead>
                                    <TableHead>Sent</TableHead>
                                    <TableHead>Opened</TableHead>
                                    <TableHead>Clicked</TableHead>
                                    <TableHead>Open rate</TableHead>
                                    <TableHead>Click rate</TableHead>
                                    <TableHead>Suppressed</TableHead>
                                    <TableHead>Failed</TableHead>
                                </TableRow>
                            </TableHeader>
                            <TableBody>
                                {stats.map(s => (
                                    <TableRow key={s.variantId}>
                                        <TableCell className="font-semibold">
                                            {s.variantId}
                                        </TableCell>
                                        <TableCell className="tabular-nums">
                                            {s.sent.toLocaleString()}
                                        </TableCell>
                                        <TableCell className="tabular-nums">
                                            {s.opened.toLocaleString()}
                                        </TableCell>
                                        <TableCell className="tabular-nums">
                                            {s.clicked.toLocaleString()}
                                        </TableCell>
                                        <TableCell className="tabular-nums">
                                            {(s.openRate * 100).toFixed(1)}%
                                        </TableCell>
                                        <TableCell className="tabular-nums">
                                            {(s.clickRate * 100).toFixed(1)}%
                                        </TableCell>
                                        <TableCell className="tabular-nums text-gray-500">
                                            {s.suppressed.toLocaleString()}
                                        </TableCell>
                                        <TableCell className="tabular-nums text-red-600">
                                            {s.failed.toLocaleString()}
                                        </TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </CardContent>
                </Card>
            )}

            <ConfirmDialog
                open={confirmDelete}
                onCancel={() => setConfirmDelete(false)}
                onConfirm={remove}
                title="Delete experiment?"
                message={`This will permanently remove "${experiment.key}" and all its variants. Existing delivery rows tagged with this experiment will keep their data.`}
                confirmLabel="Delete"
                confirmVariant="danger"
                loading={deleting}
            />
        </div>
    )
}

function ExperimentEditor({
    onCancel,
    onSaved,
}: {
    onCancel: () => void
    onSaved: (e: CommExperiment) => void | Promise<void>
}) {
    const [key, setKey] = useState('')
    const [notes, setNotes] = useState('')
    const [variants, setVariants] = useState<ExperimentVariant[]>([
        { id: 'A', weight: 50, subject: '', bodyHtml: '' },
        { id: 'B', weight: 50, subject: '', bodyHtml: '' },
    ])
    const [busy, setBusy] = useState(false)

    const updateVariant = (i: number, patch: Partial<ExperimentVariant>) => {
        setVariants(prev => prev.map((v, idx) => (idx === i ? { ...v, ...patch } : v)))
    }

    const addVariant = () => {
        const nextId = String.fromCharCode('A'.charCodeAt(0) + variants.length)
        setVariants(prev => [
            ...prev,
            { id: nextId, weight: 0, subject: '', bodyHtml: '' },
        ])
    }

    const removeVariant = (i: number) => {
        if (variants.length <= 2) return
        setVariants(prev => prev.filter((_, idx) => idx !== i))
    }

    const totalWeight = variants.reduce((s, v) => s + (v.weight || 0), 0)
    const valid = key.trim() && variants.length >= 2 && totalWeight > 0

    const submit = async () => {
        if (!valid) return
        setBusy(true)
        try {
            const created = await adminApi.createExperiment({
                key: key.trim(),
                variants: variants.map(v => ({
                    ...v,
                    subject: v.subject || null,
                    bodyHtml: v.bodyHtml || null,
                })),
                notes: notes || undefined,
                status: 'DRAFT',
            })
            await onSaved(created)
        } finally {
            setBusy(false)
        }
    }

    return (
        <div className="space-y-6">
            <button
                onClick={onCancel}
                className="flex items-center gap-1 text-sm text-gray-600 hover:text-gray-900"
            >
                <ChevronLeft className="h-4 w-4" /> Back to experiments
            </button>

            <SectionTitle
                title="New experiment"
                description="Variants are stored as JSON. Weights are normalised at send time, so they don't have to sum to 100."
                actions={
                    <PillButton variant="primary" onClick={submit} disabled={!valid || busy}>
                        {busy ? 'Creating…' : 'Create as draft'}
                    </PillButton>
                }
            />

            <div className="max-w-4xl space-y-4">
                <Card>
                    <CardContent>
                        <Field
                            label="Experiment key (immutable)"
                            hint="Lowercase, snake_case. Reference this from a broadcast's experimentKey."
                            required
                        >
                            <Input
                                value={key}
                                onChange={e => setKey(e.target.value)}
                                placeholder="e.g. welcome_subject_v1"
                            />
                        </Field>
                    </CardContent>
                </Card>

                <Card>
                    <CardContent>
                        <Field label="Notes" hint="Hypothesis, success metric, etc.">
                            <textarea
                                value={notes}
                                onChange={e => setNotes(e.target.value)}
                                rows={3}
                                className="block w-full rounded-md border border-gray-300 px-3 py-2 text-sm text-gray-900 placeholder-gray-400 shadow-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
                            />
                        </Field>
                    </CardContent>
                </Card>

                <div>
                    <div className="mb-2 flex items-center justify-between">
                        <h3 className="text-sm font-semibold text-gray-900">Variants</h3>
                        <span className="text-xs text-gray-500">
                            Total weight: {totalWeight}
                            {totalWeight === 100 ? '' : ' (will be normalised)'}
                        </span>
                    </div>
                    <div className="space-y-3">
                        {variants.map((v, i) => (
                            <Card key={i}>
                                <CardContent className="space-y-4">
                                    <div className="grid grid-cols-12 items-start gap-3">
                                        <div className="col-span-2">
                                            <Field label="ID">
                                                <Input
                                                    value={v.id}
                                                    onChange={e =>
                                                        updateVariant(i, {
                                                            id: e.target.value,
                                                        })
                                                    }
                                                />
                                            </Field>
                                        </div>
                                        <div className="col-span-2">
                                            <Field label="Weight">
                                                <Input
                                                    type="number"
                                                    value={String(v.weight)}
                                                    onChange={e =>
                                                        updateVariant(i, {
                                                            weight: Math.max(
                                                                0,
                                                                parseInt(e.target.value) || 0
                                                            ),
                                                        })
                                                    }
                                                />
                                            </Field>
                                        </div>
                                        <div className="col-span-7">
                                            <Field label="Subject (optional override)">
                                                <Input
                                                    value={v.subject ?? ''}
                                                    onChange={e =>
                                                        updateVariant(i, {
                                                            subject: e.target.value,
                                                        })
                                                    }
                                                />
                                            </Field>
                                        </div>
                                        <div className="col-span-1 pt-7">
                                            {variants.length > 2 && (
                                                <button
                                                    onClick={() => removeVariant(i)}
                                                    className="rounded p-1.5 text-gray-400 hover:bg-red-50 hover:text-red-600"
                                                    title="Remove variant"
                                                >
                                                    <X className="h-4 w-4" />
                                                </button>
                                            )}
                                        </div>
                                    </div>
                                    <Field label="Body (optional override)">
                                        <RichTextEditor
                                            value={v.bodyHtml ?? ''}
                                            onChange={val =>
                                                updateVariant(i, { bodyHtml: val })
                                            }
                                            variables={VARIANT_VARIABLES}
                                            placeholder="Variant body…"
                                            minHeight={180}
                                        />
                                    </Field>
                                </CardContent>
                            </Card>
                        ))}
                    </div>
                    <div className="mt-3">
                        <PillButton onClick={addVariant}>
                            <Plus className="h-3.5 w-3.5" /> Add variant
                        </PillButton>
                    </div>
                </div>
            </div>
        </div>
    )
}
